import React, { useEffect, useRef, useState } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { ExchangeRateService } from '../../services/exchangeRate.service';
import { DOCUMENT_CURRENCIES, isForeignCurrency } from '../../utils/fcDocuments';

interface Props {
  institutionId: string;
  currency: string;
  rate: number | '';
  /** The document date: the rate prefilled is the one in force on it. */
  asOf: string;
  onChange: (currency: string, rate: number | '') => void;
  disabled?: boolean;
  /** Help-mode keys for the two fields (data-help reaches the DOM through these). */
  currencyProps?: { 'data-help'?: string };
  rateProps?: { 'data-help'?: string };
}

/**
 * B4-C: the document currency and its rate to BDT. Picking a foreign currency prefills the rate from the exchange-rate
 * table (the rate in force on the document date); the user can edit it. A missing rate is a hint, not a block: the
 * user types one.
 */
const DocumentCurrencyFields: React.FC<Props> = ({ institutionId, currency, rate, asOf, onChange, disabled, currencyProps, rateProps }) => {
  const [hint, setHint] = useState('');
  // The rate this component last prefilled from the table. Any other filled-in rate is the document's own (a stored
  // rate arriving when an edit page loads its document, or one the user typed): it is never overwritten. A ref taken
  // at mount cannot tell, because edit pages render empty first and load the document afterwards.
  const autoRate = useRef<number | ''>('');

  useEffect(() => {
    if (!isForeignCurrency(currency) || disabled) return;
    if (rate !== '' && rate !== autoRate.current) return;
    let live = true;
    ExchangeRateService.latest(institutionId, currency, asOf)
      .then(r => {
        if (!live) return;
        autoRate.current = r.rate;
        setHint(`Rate of ${r.rateDate} from the exchange-rate table`);
        onChange(currency, r.rate);
      })
      .catch(() => { if (live) setHint(`No ${currency} rate on file for ${asOf}: enter the rate`); });
    return () => { live = false; };
  }, [currency, asOf, institutionId, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      <TextField select label="Currency" value={currency} disabled={disabled} data-help="common.currency.field.currency" {...currencyProps}
        onChange={e => { autoRate.current = ''; setHint(''); onChange(e.target.value, ''); }}
        sx={{ flex: '1 1 140px', maxWidth: 200 }}>
        {DOCUMENT_CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
      </TextField>
      {isForeignCurrency(currency) && (
        <TextField label={`Rate (BDT per ${currency})`} type="number" required value={rate} disabled={disabled} data-help="common.currency.field.rate" {...rateProps}
          onChange={e => { setHint(''); onChange(currency, e.target.value === '' ? '' : Number(e.target.value)); }}
          inputProps={{ min: 0, step: 'any' }} helperText={hint || undefined}
          sx={{ flex: '1 1 200px', maxWidth: 300 }} />
      )}
    </Box>
  );
};

export default DocumentCurrencyFields;
